import { useEffect, useState, useMemo, useCallback } from "react"
import { RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"
import { format, subDays, startOfMonth, eachDayOfInterval } from "date-fns"

import { Button } from "@f2e/ui/components/base"

import { useStore } from "@/store"
import { useGetUserMetricsApi, transformUserMetricsData } from "@/hooks/api"
import { DailyUserMetricsData } from "@/types/metrics"
import { UserMetricsTable } from "@/components/Metrics"
import { ErrorPage } from "@/components/System"

export const UserMetricsTab = () => {
  const { t } = useTranslation()
  const [userMe] = useStore((state) => [state.userMe])
  const [monthlyMetrics, setMonthlyMetrics] = useState<DailyUserMetricsData[]>(
    []
  )
  const [dailyMetrics, setDailyMetrics] = useState<DailyUserMetricsData[]>([])

  const { data: metricsData, fetchFn: getUserMetrics } = useGetUserMetricsApi(
    userMe?.id || 0
  )

  // Calculate start and end dates (60-day window)
  const dateRange = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    const currentDay = new Date().getDate()

    const today = new Date(currentYear, currentMonth, currentDay)
    const sixtyDaysAgo = subDays(today, 60)

    return {
      startDate: sixtyDaysAgo,
      endDate: today,
    }
  }, [])

  // Generate placeholder data for all 60 days (in frontend data format)
  const generateEmptyDailyMetrics = useCallback(() => {
    const days = eachDayOfInterval({
      start: dateRange.startDate,
      end: dateRange.endDate,
    })

    return days.map((day) => ({
      date: day.toISOString(),
      conversationsSubmitted: 0,
      userEmail: "",
      organizationName: "",
    }))
  }, [dateRange.startDate, dateRange.endDate])

  const refreshMetrics = useCallback(() => {
    if (!userMe?.id) return

    const formattedStartDate = format(dateRange.startDate, "yyyy-MM-dd")
    const formattedEndDate = format(dateRange.endDate, "yyyy-MM-dd")

    const params = new URLSearchParams({
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    })

    getUserMetrics({
      searchParams: params,
    })
  }, [userMe?.id, dateRange.startDate, dateRange.endDate, getUserMetrics])

  // Process metrics with placeholders to fill in any missing days
  const processMetricsWithPlaceholders = useCallback(
    (transformedMetrics: DailyUserMetricsData[]) => {
      // Generate placeholder metrics (already in camelCase format)
      const emptyMetrics = generateEmptyDailyMetrics()

      // Create a map for quick lookups by date
      const metricsMap = emptyMetrics.reduce(
        (acc, metric) => {
          // Use date string as key (yyyy-MM-dd format for consistency)
          const dateKey = format(new Date(metric.date), "yyyy-MM-dd")
          acc[dateKey] = metric
          return acc
        },
        {} as Record<string, DailyUserMetricsData>
      )

      // Merge API data with the placeholder data
      if (transformedMetrics.length > 0) {
        transformedMetrics.forEach((metric) => {
          const dateKey = format(new Date(metric.date), "yyyy-MM-dd")
          if (dateKey in metricsMap) {
            // Override placeholder with actual data
            metricsMap[dateKey] = metric
          }
        })
      } else {
        console.debug(
          "No metrics data returned, using placeholder data for all 60 days"
        )
      }

      // Convert the map back to array for processing
      const processedDailyMetrics = Object.values(metricsMap)
      setDailyMetrics(processedDailyMetrics)

      // Group by month and calculate totals
      const monthlyData = processedDailyMetrics.reduce(
        (acc, curr) => {
          const monthKey = format(new Date(curr.date), "yyyy-MM")
          if (!acc[monthKey]) {
            acc[monthKey] = {
              date: startOfMonth(new Date(curr.date)).toISOString(),
              conversationsSubmitted: 0,
              userEmail: curr.userEmail,
              organizationName: curr.organizationName,
            }
          }
          acc[monthKey].conversationsSubmitted += curr.conversationsSubmitted
          return acc
        },
        {} as Record<string, DailyUserMetricsData>
      )

      // Set monthly metrics
      setMonthlyMetrics(Object.values(monthlyData))
    },
    [generateEmptyDailyMetrics]
  )

  useEffect(() => {
    refreshMetrics()
  }, [refreshMetrics])

  // Process API response when it changes
  useEffect(() => {
    if (metricsData?.data) {
      try {
        // Transform the raw API response to our frontend data model
        const transformedData = transformUserMetricsData(metricsData.data)

        // Process metrics with placeholders
        processMetricsWithPlaceholders(transformedData.metrics)
      } catch (err) {
        console.error("Error transforming metrics data:", err)
        // Use empty metrics as fallback
        processMetricsWithPlaceholders([])
      }
    }
  }, [metricsData, processMetricsWithPlaceholders])

  if (!userMe) {
    return <ErrorPage message={t("settings.metrics.errors.userNotFound")} />
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-semibold leading-none tracking-tight">
            {t("metrics.user.title")}
          </h3>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {t("metrics.user.description")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refreshMetrics()
          }}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("common.refresh")}
        </Button>
      </div>

      <UserMetricsTable
        dailyMetrics={dailyMetrics}
        monthlyMetrics={monthlyMetrics}
      />
    </div>
  )
}

export default UserMetricsTab
